import { useCallback, useEffect, useState } from 'react';
import { PublicKey } from '@solana/web3.js';
import {
  ORE_TREASURY_ADDRESS,
  computeClaimableOre,
  decodeOreMinerSnapshot,
  decodeOreTreasurySnapshot,
  decodeTokenAccount,
  getOreMinerPda,
  getOreTokenAddress,
  inferLiveRefinedOre,
} from '../lib/evore';
import { getConnection } from '../lib/rpc';

export interface LegacyWalletBalances {
  /** Native SOL in the wallet itself — already user-custodied. */
  walletSol: bigint;
  /** ORE in the wallet's associated token account — already user-custodied, transferable. */
  ataOre: bigint;
  /** Pending SOL inside the OreMiner PDA (rewards_sol). Needs `claim_sol` to sweep to wallet. */
  rewardsSol: bigint;
  /** Pending unrefined ORE inside the OreMiner PDA (rewards_ore). */
  rewardsOre: bigint;
  /** Live refined ORE (folds in the treasury-factor-inferred accrual since last checkpoint). */
  liveRefinedOre: bigint;
  /** What the user receives if they claim right now: 90% of rewardsOre + liveRefinedOre. */
  claimableOre: bigint;
  /** True iff the wallet has an OreMiner PDA on-chain. */
  hasOreMiner: boolean;
  /** True iff the wallet has an ORE associated token account on-chain. */
  hasAta: boolean;
}

export interface LegacyWalletState {
  loading: boolean;
  error: string | null;
  balances: LegacyWalletBalances | null;
  /** Wall-clock time of the most recent successful load. */
  lastUpdatedAt: Date | null;
  refresh: () => Promise<void>;
}

/**
 * Read-only balance fetch for a connected wallet's legacy mining state.
 *
 * "Legacy" = ORE mined directly into the wallet's own OreMiner PDA on the ORE
 * program (no Evore subaccount intermediary). Old-timer Privy users who mined
 * before subaccounts existed accrued state here that's never been claimed.
 *
 * Manual refresh only (no polling) — see memory `sunset-no-rolling-refresh`.
 * RPC-only (no backend) — see memory `sunset-rpc-only`. Single
 * `getMultipleAccountsInfo` batch covers the wallet, OreMiner PDA, ORE
 * treasury, and ORE ATA.
 */
export function useLegacyWallet(wallet: PublicKey | null): LegacyWalletState {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [balances, setBalances] = useState<LegacyWalletBalances | null>(null);
  const [lastUpdatedAt, setLastUpdatedAt] = useState<Date | null>(null);

  const load = useCallback(async () => {
    if (!wallet) {
      setBalances(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const connection = getConnection();
      const [oreMinerPda] = getOreMinerPda(wallet);
      const ataAddress = getOreTokenAddress(wallet);

      const [walletInfo, minerInfo, treasuryInfo, ataInfo] =
        await connection.getMultipleAccountsInfo([
          wallet,
          oreMinerPda,
          ORE_TREASURY_ADDRESS,
          ataAddress,
        ]);

      const walletSol = walletInfo ? BigInt(walletInfo.lamports) : 0n;

      let rewardsSol = 0n;
      let rewardsOre = 0n;
      let liveRefinedOre = 0n;
      let claimableOre = 0n;
      const hasOreMiner =
        !!minerInfo && minerInfo.data && minerInfo.data.length >= 752;

      if (hasOreMiner) {
        try {
          const snapshot = decodeOreMinerSnapshot(minerInfo!.data);
          rewardsSol = snapshot.rewardsSol;
          rewardsOre = snapshot.rewardsOre;
          const treasury = treasuryInfo
            ? decodeOreTreasurySnapshot(treasuryInfo.data)
            : null;
          liveRefinedOre = treasury
            ? inferLiveRefinedOre(snapshot, treasury)
            : snapshot.refinedOre;
          claimableOre = treasury
            ? computeClaimableOre(snapshot, treasury)
            : (rewardsOre * 9n) / 10n + snapshot.refinedOre;
        } catch (decodeErr) {
          console.warn('[useLegacyWallet] OreMiner decode failed:', decodeErr);
        }
      }

      let ataOre = 0n;
      const hasAta = !!ataInfo && ataInfo.data && ataInfo.data.length >= 72;
      if (hasAta) {
        try {
          ataOre = decodeTokenAccount(ataInfo!.data).amount;
        } catch (decodeErr) {
          console.warn('[useLegacyWallet] ATA decode failed:', decodeErr);
        }
      }

      setBalances({
        walletSol,
        ataOre,
        rewardsSol,
        rewardsOre,
        liveRefinedOre,
        claimableOre,
        hasOreMiner,
        hasAta,
      });
      setLastUpdatedAt(new Date());
    } catch (err) {
      console.error('[useLegacyWallet] load failed:', err);
      setError(
        err instanceof Error ? err.message : 'sunset.errors.loadLegacyFailed'
      );
      setBalances(null);
    } finally {
      setLoading(false);
    }
  }, [wallet]);

  useEffect(() => {
    void load();
  }, [load]);

  return { loading, error, balances, lastUpdatedAt, refresh: load };
}
